import React from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import { SxProps } from '@mui/system';
import { AntDesign } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { SizableText } from 'tamagui';

interface DefaultButtonProps {
  onPress: () => void;
  buttonText?: JSX.Element | string;
  disabled?: boolean;
  icon?: JSX.Element | string;
  defaultColor?: string;
  onPressColor?: string;
  onHoverColor?: string;
  portrait?: boolean;
  outline?: boolean;
  customStyle?: SxProps,
  iconColor?: string,
  textColor?: string,
  badge?: number | string,
  isClose?: boolean,
  iconOnly?: boolean,
  inverse?: boolean,
  noShadow?: boolean,
  removeBorder?: boolean,
  iconStyle?: React.CSSProperties,
  iconSize?: number,
  children?: React.ReactNode | string,
  textStyle?: React.CSSProperties,
}

const MaterialUIButton: React.FC<DefaultButtonProps> = ({
  onPress,
  buttonText,
  disabled = false,
  icon,
  removeBorder = false,
  isClose = false,
  iconColor,
  defaultColor = '#487fff',
  onPressColor = '#99DBF7',
  portrait = false,
  outline = false,
  customStyle,
  textColor = "white",
  badge,
  iconOnly,
  inverse,
  iconSize = 24,
  noShadow = false,
  iconStyle,
  textStyle,
  children,
  ...props
}) => {
  const theme = themeSelector();

  const buttonSx: SxProps = {
    alignSelf: 'center',
    minWidth: !iconOnly ? 70 : 30,
    maxWidth: iconOnly ? 60 : undefined,
    minHeight: portrait ? 80 : 30,
    maxHeight: portrait ? 80 : 30,
    borderRadius: removeBorder ? 0 : 16,
    boxShadow: noShadow ? 'none' : undefined,
    color: textColor || (theme ? 'black' : 'white'),
    fontWeight:"bold",
    bgcolor: disabled ? "#333333" : defaultColor,
    '&:hover': {
      bgcolor: onPressColor,
    },
    ...customStyle,
    padding: 3
  };

  const content = buttonText && typeof buttonText === 'string'
    ? <SizableText style={{ color:"white", ...textStyle, fontWeight:'bold', }}>{buttonText}</SizableText>
    : children || buttonText;

  const iconElement = typeof icon === 'string'
    ? <AntDesign name={icon} size={iconSize} color={iconColor || 'black'} style={[{ alignSelf: 'center', textAlign: "center", justifyContent: 'center' }, iconStyle]} />
    : icon;

  if (iconOnly) {
    return (
      <IconButton
        onClick={onPress}
        disabled={disabled}
        sx={buttonSx}
        {...props}
      >
        {badge != null ? (
          <Badge badgeContent={badge} color="error">
            {iconElement}
          </Badge>
        ) : iconElement}
      </IconButton>
    );
  }

  return (
    <Button
      onClick={onPress}
      disabled={disabled}
      variant={outline ? 'outlined' : 'contained'}
      sx={buttonSx}
      startIcon={inverse ? undefined : iconElement}
      endIcon={inverse ? iconElement : undefined}
      {...props}
    >
      {badge != null ? (
        <Badge badgeContent={badge} color="error">
          {content}
        </Badge>
      ) : content}
    </Button>
  );
};

export default MaterialUIButton;
